import { IconButton } from "@material-ui/core";
import { observer } from "mobx-react";
import * as React from "react";
import { THEME } from "../../../theme/ThemeStore";
import { rejectedDateRangeOptions, getJobCategoryOptions, Option } from "../../../constants/index";
import { t } from "../../../i18n/util";
import { hrStore } from "../../../stores/HrStore";
import { useTableStore } from "../../../stores/TableStore";
import { DateRangeEnum, JobCategoryEnum } from "../../../types/models";
import { useMobileMediaQuery } from "../../hooks/useMobileMediaQuery";
import { useScrollBlock } from "../../hooks/useScrollBlock";
import { useTableSelection } from "../../hooks/useTableSelection";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { HrBotBackButton, HrBotButton, HrBotGreyButtonSimple, HrBotGreyButtonToggles } from "../../ui/Primitives";
import { BurgerMenu, MobileMenuElementContainer } from "../HrNavBarElements";
import { RejectedList } from "../RejectedList";
import { HrRoutes } from "../router/HrRoutes";
import { get } from "mobx";

export const RejectedSite = observer(() => {
    const isMobile = useMobileMediaQuery();

    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
    const [openMobileFilterMenu, setOpenMobileFilterMenu] = React.useState(false);

    // enables and disables delete and move buttons
    const [someSelected, setSomeSelected] = React.useState(false);

    const [blockScroll, allowScroll] = useScrollBlock();

    // blocks scroll when menu is open
    React.useEffect(() => {
        if (isMobile && openMobileFilterMenu) {
            blockScroll();
        } else {
            allowScroll();
        }
    }, [allowScroll, blockScroll, isMobile, openMobileFilterMenu]);

    // saves where to go on "back" from details view
    React.useEffect(() => {
        hrStore.backTarget = HrRoutes.REJECTED;
    }, []);

    const tableStore = useTableStore("rejected", {
        orderBy: "createdAt",
        orderDir: "asc"
    });

    const selectJobCategory = useTableSelection(
        getJobCategoryOptions().map((jobCategoryOption) => ({ id: jobCategoryOption.value })),
        hrStore.selectedJobCategoryIds.length > 0 ? hrStore.selectedJobCategoryIds : "all"
    );

    React.useEffect(() => {
        // save filter to store in order to persist it
        hrStore.selectedJobCategoryIds = selectJobCategory.selectedIds;
    }, [selectJobCategory.selectedIds]);

    const memoizedJobCategories = React.useMemo(
        () => (!selectJobCategory.allSelected ? (selectJobCategory.selectedIds as JobCategoryEnum[]) : []),
        [selectJobCategory.allSelected, selectJobCategory.selectedIds]
    );

    const handleCloseJobCategory = () => {
        selectJobCategory.selectedIds.length === 0 && selectJobCategory.toggleSelectAll();
    };

    const handleSelectDateRange = (dateRange: Option<DateRangeEnum>) => {
        hrStore.rejectedLastChangeSince = dateRange;
    };

    const handleResetFilters = () => {
        hrStore.rejectedLastChangeSince = rejectedDateRangeOptions[1];
        !selectJobCategory.allSelected && selectJobCategory.toggleSelectAll();
    };

    //#region dialog handling
    const handleOpenDeleteDialog = () => {
        setOpenDeleteDialog(true);
    };
    //#endregion

    const tableHeader = isMobile ? (
        <>
            <div
                style={{
                    display: "flex",
                    paddingBottom: 16,
                    flexDirection: "column",
                    gap: 16
                }}
            >
                <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                    <tableStore.SearchField placeholder={t("screen.evidence.search")} />
                    <IconButton onClick={() => setOpenMobileFilterMenu(true)}>
                        <img src={THEME.Images.icFilter} alt="" />
                    </IconButton>
                </div>
                <div style={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
                    <IconButton disabled={!someSelected} onClick={handleOpenDeleteDialog}>
                        <img src={THEME.Images.icDelete} alt="" style={{ opacity: !someSelected ? 0.25 : 1 }} />
                    </IconButton>
                </div>
            </div>
            <BurgerMenu open={openMobileFilterMenu} style={{ zIndex: 11, paddingTop: 0 }}>
                <MobileMenuElementContainer>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            width: "100%",
                            padding: "0 16px"
                        }}
                    >
                        <div style={{ width: 24 }} />
                        <h3>{t("screen.evidence.filterOptions")}</h3>
                        <img
                            src={THEME.Images.icClose}
                            alt=""
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                                setOpenMobileFilterMenu(false);
                            }}
                        />
                    </div>
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotGreyButtonToggles
                        sorted
                        descriptionText={t("screen.dashboard.division")}
                        onClose={handleCloseJobCategory}
                        options={getJobCategoryOptions()}
                        select={selectJobCategory}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotGreyButtonSimple
                        value={hrStore.rejectedLastChangeSince}
                        descriptionText={t("screen.evidence.show")}
                        options={rejectedDateRangeOptions}
                        onSelect={handleSelectDateRange}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotButton
                        label={t("screen.evidence.saveFilterOptions")}
                        type="button"
                        style={{ width: "fit-content" }}
                        onClick={() => setOpenMobileFilterMenu(false)}
                    />
                </MobileMenuElementContainer>
                <MobileMenuElementContainer>
                    <HrBotBackButton
                        label={t("screen.evidence.resetFilterOptions")}
                        style={{ width: "fit-content", padding: "18px 26px" }}
                        onClick={() => {
                            setOpenMobileFilterMenu(false);
                            handleResetFilters();
                        }}
                    />
                </MobileMenuElementContainer>
            </BurgerMenu>
        </>
    ) : (
        <div
            style={{
                display: "flex",
                paddingBottom: 36,
                alignItems: "center",
                justifyContent: "space-between"
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 36
                }}
            >
                <HrBotGreyButtonToggles
                    sorted
                    descriptionText={t("screen.dashboard.division")}
                    onClose={handleCloseJobCategory}
                    options={getJobCategoryOptions()}
                    select={selectJobCategory}
                />
                <HrBotGreyButtonSimple
                    value={hrStore.rejectedLastChangeSince}
                    descriptionText={t("screen.evidence.show")}
                    options={rejectedDateRangeOptions}
                    onSelect={handleSelectDateRange}
                />
            </div>
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    gap: 16,
                    flexGrow: 1
                }}
            >
                <IconButton disabled={!someSelected} onClick={handleOpenDeleteDialog} style={{ padding: 0 }}>
                    <img src={THEME.Images.icDelete} alt="" style={{ opacity: !someSelected ? 0.25 : 1 }} />
                </IconButton>
                <tableStore.SearchField placeholder={t("screen.evidence.search")} maxWidth />
            </div>
        </div>
    );

    return (
        <div
            style={{ backgroundColor: THEME.customColors.backgroundLight, width: "100%", flexGrow: 1, display: "flex" }}
        >
            <ContentWrapper style={{ padding: isMobile ? 20 : "36px 56px 0", maxWidth: 1600, flexGrow: 1 }}>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        flexGrow: 1,
                        justifyContent: "space-between",
                        marginBottom: 50,
                        gap: 32
                    }}
                >
                    <div style={{ width: "100%" }}>
                        {tableHeader}
                        <RejectedList
                            jobCategories={memoizedJobCategories}
                            lastChangeSince={hrStore.rejectedLastChangeSince}
                            tableStore={tableStore}
                            openDeleteDialog={openDeleteDialog}
                            setOpenDeleteDialog={setOpenDeleteDialog}
                            setSomeSelected={setSomeSelected}
                        />
                    </div>
                    {tableStore.totalCount !== 0 && <tableStore.Pagination isMobile={isMobile} />}
                </div>
            </ContentWrapper>
        </div>
    );
});
